import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronsUpDown, LogOut, User } from 'lucide-react'
import { BrandLockup } from '@/components/app/brand-lockup'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarResizeHandle,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { can, type Role } from '@/core/access'
import { adminNavItems, isNavItemActive, type NavItem, navSections } from './nav-config'

export interface AppSidebarProps {
  items: NavItem[]
  counts?: Record<string, number>
  onSignOut?: () => void
  account?: AccountSummary
}

/** Who is signed in, as the account row shows it. */
export interface AccountSummary {
  name: string
  email: string
  initials: string
  /** Decides which admin destinations appear. docs/PERMISSIONS.md. */
  role: Role
}

export function AppSidebar({ items, counts, onSignOut, account }: AppSidebarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  // Hidden until the role is known, so Staff never see a flash of admin links.
  const role = account?.role
  const allowed = (permission: Parameters<typeof can>[1]) =>
    role !== undefined && can(role, permission)

  return (
    <Sidebar collapsible="icon">
      {/* The app's sections as a landmark, so a screen reader can jump to them. */}
      <nav aria-label="Main menu" className="flex h-full min-h-0 flex-col">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" render={<Link to="/" />}>
                <BrandLockup tone="sidebar" showWorkspace />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          {navSections(items).map((section) => (
            <SidebarGroup key={section.group ?? 'main'}>
              {section.group ? (
                // A plain heading with a thin rule running beside it. The sidebar
                // can be any brand colour, so both use the sidebar's own
                // foreground token (the 85% text passes contrast on Fir green).
                <SidebarGroupLabel className="gap-2 text-sidebar-foreground/85">
                  <span>{section.group}</span>
                  <span aria-hidden="true" className="h-px flex-1 bg-sidebar-foreground/25" />
                </SidebarGroupLabel>
              ) : null}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => {
                    const count = item.badge ? (counts?.[item.id] ?? 0) : 0
                    return (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          tooltip={item.title}
                          isActive={isNavItemActive(pathname, item.url)}
                          render={<Link to={item.url} />}
                        >
                          <item.icon />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                        {count > 0 ? <SidebarMenuBadge>{count}</SidebarMenuBadge> : null}
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>

        {/* A hairline above the fixed group: on a short screen the list above
          scrolls, and runs visibly under it rather than just ending. */}
        <SidebarFooter className="border-t border-sidebar-border">
          <SidebarMenu>
            {/* Admin destinations (nav-config's adminNavItems). Staff see none. */}
            {adminNavItems
              .filter((link) => allowed(link.permission))
              .map((link) => (
                <SidebarMenuItem key={link.id}>
                  <SidebarMenuButton
                    tooltip={link.title}
                    isActive={link.isActive(pathname)}
                    render={<Link to={link.url} />}
                  >
                    <link.icon />
                    <span>{link.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger render={<SidebarMenuButton size="lg" />}>
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">
                      {account?.initials ?? ''}
                    </AvatarFallback>
                  </Avatar>
                  {/* The real signed-in person. It used to be a hard-coded demo
                    user, which on the live site meant everyone saw "Ava Chen". */}
                  {account ? (
                    <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{account.name}</span>
                      <span className="text-sidebar-foreground/85 truncate text-xs">
                        {account.email}
                      </span>
                    </div>
                  ) : (
                    <div className="grid flex-1 gap-2">
                      <Skeleton className="h-3.5 w-24" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  )}
                  <ChevronsUpDown className="ml-auto size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="min-w-56">
                  <DropdownMenuItem render={<Link to="/account" />}>
                    <User />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onSignOut}>
                    <LogOut />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </nav>

      <SidebarRail />
      <SidebarResizeHandle />
    </Sidebar>
  )
}
